import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { requestNotificationPermission, hasNotificationPermission } from "../utils/browserNotifications";
import api from "../services/api";
import { LIVE_POLL, livePollInterval } from "../config/liveSync";

function isBackendUnreachable(error: unknown): boolean {
  const e = error as { code?: string; message?: string; response?: unknown };
  return (
    !e?.response &&
    (e?.code === "ERR_NETWORK" ||
      e?.code === "ECONNABORTED" ||
      e?.message === "Network Error")
  );
}

const getNotificationId = (n: { _id?: string; id?: string }) =>
  n._id || n.id || '';

interface Notification {
  _id: string;
  id?: string;
  type: 'order' | 'offer' | 'bid' | 'product' | 'account' | 'promotional' | 'admin_message';
  title: string;
  message: string;
  body?: string;
  payload?: any;
  data?: any;
  link?: string;
  channel?: 'websocket' | 'push' | 'email' | 'in-app';
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Hook to manage in-app notifications for admin
 * - Fetches notifications from backend
 * - Handles read/unread status
 * - Supports marking as read, deleting, and clearing all
 * - Polls the admin notifications API (no Socket.IO on backend)
 */
export const useNotifications = () => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const isAuthenticated = !!token;

  // Track last notification ID for optimized polling
  const lastNotificationIdRef = useRef<string | null>(null);

  // Request browser notification permission on mount
  useEffect(() => {
    if (isAuthenticated && !hasNotificationPermission()) {
      requestNotificationPermission().then((granted) => {
        if (granted) {
          console.log('✅ Browser notification permission granted');
        }
      });
    }
  }, [isAuthenticated]);

  // Fetch notifications - only if admin is authenticated
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      try {
        const response = await api.get("/admin/notifications");
        if (response.data?.status === "success" && response.data?.data) {
          const fetchedNotifications = response.data.data;

          const adminNotifications = fetchedNotifications.filter((n: Notification) => {
            const dataType = String((n.payload || n.data)?.type || "");
            const isOffer =
              n.type === "bid" ||
              n.type === "offer" ||
              dataType.includes("bid") ||
              dataType.includes("offer");
            if (isOffer) return false;
            return (
              n.type === "order" ||
              n.type === "product" ||
              n.type === "admin_message" ||
              dataType.includes("order") ||
              dataType.includes("product")
            );
          });

          if (adminNotifications.length > 0) {
            const latestNotification = adminNotifications[0];
            const latestId = latestNotification._id || latestNotification.id;
            if (latestId !== lastNotificationIdRef.current) {
              lastNotificationIdRef.current = latestId;
            }
          }

          return adminNotifications;
        }
        return [];
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) return [];
        if (isBackendUnreachable(err)) {
          // Let React Query mark error and slow polling; keep last data via placeholderData
          throw err;
        }
        if (status !== 401 && status !== 403) {
          console.error("Failed to fetch notifications:", err);
        }
        return [];
      }
    },
    enabled: !!isAuthenticated,
    retry: false,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 10,
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      isBackendUnreachable(query.state.error)
        ? 120_000
        : livePollInterval(LIVE_POLL.NOTIFICATIONS) || LIVE_POLL.NOTIFICATIONS,
    refetchOnWindowFocus: (query) => !isBackendUnreachable(query.state.error),
    refetchOnReconnect: true,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
  });

  // Calculate unread count
  const unreadCount = useMemo(() => {
    return notifications.filter((n: Notification) => !n.isRead).length;
  }, [notifications]);

  // Mark single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await api.patch(`/admin/notifications/${notificationId}/read`);
      return response.data;
    },
    onSuccess: (_data: unknown, notificationId: string) => {
      queryClient.setQueryData(["notifications"], (old: Notification[] = []) =>
        old.map((n) =>
          getNotificationId(n) === notificationId ? { ...n, isRead: true } : n
        )
      );
    },
  });

  // Mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await api.patch('/admin/notifications/read-all');
      return response.data;
    },
    onSuccess: () => {
      // Update all notifications to read in cache
      queryClient.setQueryData(["notifications"], (old: Notification[] = []) =>
        old.map((n) => ({ ...n, isRead: true }))
      );
    },
  });

  // Delete single notification
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await api.delete(`/admin/notifications/${notificationId}`);
      return response.data;
    },
    onSuccess: (_data: any, notificationId: string) => {
      // Remove from cache
      queryClient.setQueryData(["notifications"], (old: Notification[] = []) =>
        old.filter((n) => getNotificationId(n) !== notificationId)
      );
    },
  });

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    refetch,
    markAsRead: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    deleteNotification: deleteNotificationMutation.mutateAsync,
    isMarkingAsRead: markAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending,
  };
};

export default useNotifications;

